/**
 * Tek kaynak: site sabitleri + JSON dosyalarından gelen eski (Elit) verileri güvenli şekilde temizleme.
 */

export const SITE_NAME = 'Tonrex IPTV';
export const SITE_URL = 'https://tonrex.com';

/** Sosyal paylaşım görseli 1200×630 (public/) */
export const DEFAULT_OG_IMAGE = '/og-image.svg';

/** Göreli yol veya tam URL → canonical site URL */
export function absoluteUrl(path) {
  if (!path) return SITE_URL;
  if (typeof path === 'string' && path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${p}`;
}

export const DEFAULT_CONTACT_EMAIL = 'iletisim@tonrex.com';
export const DEFAULT_WHATSAPP_DISPLAY = '555 555 55 55';
/** wa.me ve yedek yönlendirmeler için uluslararası rakam dizisi */
export const DEFAULT_WHATSAPP_DIGITS = '905555555555';

export const DEFAULT_APP_LINKS = {
  windowsPC: 'https://tonrex.com/indir/windows',
  androidXC: 'https://tonrex.com/indir/android-xc',
  androidSmarters: 'https://tonrex.com/indir/android-smarters',
  playStoreXC: 'https://play.google.com/store/apps',
  playStoreSmarters: 'https://play.google.com/store/apps',
  iosApp: 'https://apps.apple.com'
};

export const DEFAULT_APP_NAMES = {
  windowsPC: 'Tonrex IPTV PC',
  androidXC: 'Tonrex XC',
  androidSmarters: 'Tonrex Smarters',
  iosApp: 'Tonrex Player'
};

export const DEFAULT_SEO = {
  siteTitle: `${SITE_NAME} | Canlı TV, Film ve Dizi`,
  siteDescription:
    'Tonrex IPTV ile canlı TV, film ve dizi izleyin. TR ve EU paketleri, kurulum rehberi ve WhatsApp destek hattı.',
  siteKeywords:
    'iptv satın al, iptv abonelik, tonrex iptv, iptv paket, iptv fiyatları, iptv paketleri, canlı tv, 4k iptv, güvenilir iptv',
  siteUrl: SITE_URL,
  /** Navbar / footer marka metni */
  brandName: SITE_NAME,
  /** Boş veya `/uploads/site-logo.png` — public veya tam URL */
  logoUrl: '',
  showLogoImage: true,
  showBrandText: false
};

const LEGACY_BRAND_PATTERN = /\belit\b|net iptv|elitiptv/i;

/** SEO + marka alanlarını birleştirir */
export const sanitizeSeoSettings = (raw) => {
  const base = { ...DEFAULT_SEO };
  if (!raw || typeof raw !== 'object') return base;
  const out = { ...base, ...raw };
  if (typeof out.siteTitle === 'string' && out.siteTitle.trim()) {
    out.siteTitle = out.siteTitle.trim();
  } else {
    out.siteTitle = base.siteTitle;
  }
  if (typeof out.siteDescription === 'string') out.siteDescription = out.siteDescription.trim() || base.siteDescription;
  if (typeof out.siteKeywords === 'string') out.siteKeywords = out.siteKeywords.trim() || base.siteKeywords;
  if (typeof out.siteUrl === 'string' && out.siteUrl.trim()) out.siteUrl = out.siteUrl.trim();
  else out.siteUrl = base.siteUrl;

  let brandName = typeof raw.brandName === 'string' ? raw.brandName.trim() : '';
  if (!brandName || LEGACY_BRAND_PATTERN.test(brandName)) brandName = DEFAULT_SEO.brandName;
  out.brandName = brandName;

  let logoUrl = typeof raw.logoUrl === 'string' ? raw.logoUrl.trim() : '';
  if (logoUrl && LEGACY_BRAND_PATTERN.test(logoUrl)) logoUrl = '';
  out.logoUrl = logoUrl;
  out.showLogoImage = raw.showLogoImage !== false;
  out.showBrandText = raw.showBrandText !== false;
  return out;
};

/** Navbar / footer için görüntülenecek marka */
export const getSiteBranding = (seoSettings) => {
  const s = sanitizeSeoSettings(seoSettings);
  return {
    brandName: s.brandName,
    logoUrl: s.logoUrl,
    showLogoImage: s.showLogoImage && Boolean(s.logoUrl),
    showBrandText: s.showBrandText
  };
};

/** Paketler sayfası platform şeridi — bkz. src/config/platformLogos.js */
export {
  PLATFORM_LOGO_LOCAL as PLATFORM_LOGO_PATHS,
  PLATFORM_LOGO_REMOTE,
  PLATFORM_LOGO_ALTS
} from './platformLogos';

/** Eski marka / alan adı / mağaza paketleri (kayıtlı veride kalmış olabilir) */
const LEGACY_LINK_PATTERN =
  /elitiptv|elitdigital|elitplus|nathnetwork|tinyurl\.com\/elit|4916389|8926744|elitelk|elitpc|elitmedia|000-player\/id1665441224|example\.com\/indir/i;

const LEGACY_APP_NAME_PATTERN = /\belit\b|elite\+\+|ELIT\s|Net IPTV/i;

export function isLegacyDownloadUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return true;
  return LEGACY_LINK_PATTERN.test(value);
}

export function isLegacyWhatsapp(value) {
  const d = String(value || '').replace(/\D/g, '');
  if (!d) return false;
  if (d.includes('491638926744')) return true;
  if (d.startsWith('49163') && d.length >= 11) return true;
  if (d.startsWith('49') && d.includes('163')) return true;
  return false;
}

export function isLegacyEmail(value) {
  return /elitelk|@elitiptv|elitiptv\.net|@example\.com/i.test(String(value || ''));
}

/** Görünen telefon metninden wa.me için uluslararası rakam dizisi üretir.
 *  0555 555 55 55 → 905555555555
 *  90555 555 55 55 → 905555555555
 *  +49 163 1234567 → 491631234567
 */
export function whatsappToWaDigits(display, fallback = DEFAULT_WHATSAPP_DIGITS) {
  let d = (display || '').replace(/[^0-9]/g, '');
  if (!d) return fallback;
  if (d.startsWith('0')) d = d.slice(1);
  if (d.length === 10 && !d.startsWith('90')) return `90${d}`;
  return d;
}

/**
 * `links/download` dokümanı: eski Elit URL’lerini varsayılanlarla değiştirir.
 */
export const sanitizeStoredLinks = (raw) => {
  const keys = Object.keys(DEFAULT_APP_LINKS);
  const base = { ...DEFAULT_APP_LINKS };
  if (!raw || typeof raw !== 'object') {
    return { ...base, appNames: { ...DEFAULT_APP_NAMES } };
  }
  const out = { ...raw };
  for (const k of keys) {
    out[k] = isLegacyDownloadUrl(raw[k]) ? DEFAULT_APP_LINKS[k] : String(raw[k]).trim() || DEFAULT_APP_LINKS[k];
  }
  const names = { ...DEFAULT_APP_NAMES };
  if (raw.appNames && typeof raw.appNames === 'object') {
    for (const k of Object.keys(DEFAULT_APP_NAMES)) {
      const n = raw.appNames[k];
      if (typeof n === 'string' && n.trim() && !LEGACY_APP_NAME_PATTERN.test(n)) {
        names[k] = n.trim();
      }
    }
  }
  out.appNames = names;
  return out;
};

/** Admin formu için link + uygulama adı alanları */
export function pickLinkFormFields(sanitized) {
  const s = sanitizeStoredLinks(sanitized);
  return {
    links: {
      windowsPC: s.windowsPC,
      androidXC: s.androidXC,
      androidSmarters: s.androidSmarters,
      playStoreXC: s.playStoreXC,
      playStoreSmarters: s.playStoreSmarters,
      iosApp: s.iosApp
    },
    appNames: { ...DEFAULT_APP_NAMES, ...s.appNames }
  };
}

export const DEFAULT_CONTACT_ADDRESS = {
  street: '',
  city: 'İstanbul',
  postalCode: '',
  country: 'Türkiye'
};

export const DEFAULT_CONTACT_INFO = {
  whatsapp: DEFAULT_WHATSAPP_DISPLAY,
  email: DEFAULT_CONTACT_EMAIL,
  address: { ...DEFAULT_CONTACT_ADDRESS },
  workingHours: 'WhatsApp Destek Hattı'
};

/** Footer / Hakkımızda için tek satır adres */
export function formatContactAddress(address) {
  if (!address || typeof address !== 'object') return '';
  const parts = [];
  const street = String(address.street || '').trim();
  if (street) parts.push(street);
  const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ').trim();
  if (cityLine) parts.push(cityLine);
  const country = String(address.country || '').trim();
  if (country) parts.push(country);
  if (parts.length) return parts.join(', ');
  return [address.city, address.country].filter(Boolean).join(', ');
}

/**
 * `content/contactInfo`: eski Almanya hattı / Elit e-postasını varsayılanlarla değiştirir;
 * adres alanlarını her zaman tam nesne olarak döndürür (admin editörü + footer uyumu).
 */
export const sanitizeStoredContact = (raw) => {
  const base = {
    ...DEFAULT_CONTACT_INFO,
    address: { ...DEFAULT_CONTACT_ADDRESS }
  };
  if (!raw || typeof raw !== 'object') return base;

  let whatsapp =
    typeof raw.whatsapp === 'string' && raw.whatsapp.trim()
      ? raw.whatsapp.trim()
      : base.whatsapp;
  let email =
    typeof raw.email === 'string' && raw.email.trim() ? raw.email.trim() : base.email;
  if (isLegacyWhatsapp(whatsapp)) whatsapp = DEFAULT_WHATSAPP_DISPLAY;
  if (isLegacyEmail(email)) email = DEFAULT_CONTACT_EMAIL;

  const addrRaw = raw.address && typeof raw.address === 'object' ? raw.address : {};
  const address = {
    street: typeof addrRaw.street === 'string' ? addrRaw.street.trim() : '',
    city:
      typeof addrRaw.city === 'string' && addrRaw.city.trim()
        ? addrRaw.city.trim()
        : base.address.city,
    postalCode: typeof addrRaw.postalCode === 'string' ? addrRaw.postalCode.trim() : '',
    country:
      typeof addrRaw.country === 'string' && addrRaw.country.trim()
        ? addrRaw.country.trim()
        : base.address.country
  };

  const workingHours =
    typeof raw.workingHours === 'string' && raw.workingHours.trim()
      ? raw.workingHours.trim()
      : base.workingHours;

  const out = { whatsapp, email, address, workingHours };
  if (raw.updatedAt) out.updatedAt = raw.updatedAt;
  return out;
};
